import React, { useEffect, useState } from 'react';
import { PhoneOff, Mic, MicOff, Minus, AlertCircle, Phone, Radio } from 'lucide-react';
import { useStudioLineContext } from '../context/StudioLineContext';
import { useRingtone } from '../hooks/useRingtone';
import { useStudioCallVoice } from '../hooks/useStudioCallVoice';
import { useAudio } from '../context/AudioContext';

/**
 * شاشة المكالمة.
 *
 * البطاقة الصغيرة داخل صفحة الاستديو كانت تكفي لطلب الاتصال، لا للمكالمة
 * نفسها: من يتحدث على الهواء لا يتصفّح، ويحتاج أن يرى حاله وزرّ الإنهاء
 * بلا بحث. فالمكالمة تأخذ الشاشة كلها، كما يفعل الهاتف.
 *
 * وهي لكلا الطرفين: المتصل يرى موقعه في الطابور ثم من يحدّثه، والمذيع
 * يرى اسم المتصل وموضوعه وجواله أمامه طوال المكالمة.
 */

const clock = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

export const StudioCallScreen = () => {
  const { call, onCall, isAdmin, ahead, endCall, cancelCall, transferCall, errorMessage, clearError } =
    useStudioLineContext();

  const [isMuted, setIsMuted] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const isActive = call?.state === 'active';
  const isWaiting = call?.state === 'waiting';

  /*
    الإذاعة تسكت حين يبدأ الكلام.

    كانت تبقى تعمل تحت المكالمة، فيلتقطها الميكروفون ويسمعها المذيع فوق
    صوت المتصل. والمجلس يوقفها عند دخوله؛ المكالمة كذلك، عند الردّ لا
    أثناء الانتظار — نغمة الانتظار تسمعها فوقها ولا ضرر.
  */
  const { pauseAudio } = useAudio();
  useEffect(() => {
    if (isActive) pauseAudio();
    // pauseAudio تُعاد كل رندر؛ الإيقاف مرة عند بدء المكالمة هو المقصود
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  /*
    الانتظار لا يكون صامتاً.

    المتصل يسمع نغمة انتظار حتى يُردّ عليه، والمذيع يسمع رنيناً ما دامت
    مكالمة تنتظره. وقبلها كانت الشاشة صامتة تماماً، فيظنّ الطرفان أن
    الصوت معطّل وإنما هو غائب.
  */
  useRingtone('/ring-wait.mp3', isWaiting && !isAdmin, { volume: 0.4 });

  const { voiceStatus, voiceError, isVoiceLive, otherSpeaking } = useStudioCallVoice({
    callId: call?.id ?? null,
    active: isActive,
    isWaiting: isWaiting && !isAdmin,
    isMuted,
  });

  // المؤقّت يُحسب محلياً من لحظة بدء المكالمة على هذا الجهاز.
  // الاعتماد على وقت الخادم يجعله يقفز أو يجمد إن خالفت ساعة الجهاز ساعته.
  const activeId = call?.state === 'active' ? call.id : null;
  useEffect(() => {
    if (!activeId) {
      setElapsed(0);
      return;
    }
    const startedAt = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed((Date.now() - startedAt) / 1000), 1000);
    return () => clearInterval(t);
  }, [activeId]);

  // كل مكالمة تبدأ بميكروفون مفتوح وشاشة كاملة
  useEffect(() => {
    if (!onCall) {
      setIsMuted(false);
      setMinimized(false);
    }
  }, [onCall]);

  useEffect(() => {
    if (!errorMessage) return;
    const t = setTimeout(clearError, 5000);
    return () => clearTimeout(t);
  }, [errorMessage, clearError]);

  if (!onCall || !call) return null;

  const title = isAdmin ? call.callerName : 'استديو الإذاعة';
  const initial = (title || '؟').trim().charAt(0);

  /* ------------------------------ مصغّرة ------------------------------ */

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed top-0 left-0 right-0 z-[70] safe-top animate-fadeIn"
      >
        <span className="mx-3 mt-2 h-11 px-4 rounded-full bg-primary text-primary-ink shadow-lift flex items-center gap-3">
          <Phone className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span className="text-xs font-semibold truncate flex-1 text-right">{title}</span>
          <span className="text-xs tabular-nums flex-shrink-0">
            {isActive ? clock(elapsed) : 'في الانتظار'}
          </span>
        </span>
      </button>
    );
  }

  /* ------------------------------ كاملة ------------------------------ */

  const stateLine = isWaiting
    ? isAdmin
      ? 'مكالمة في الانتظار'
      : ahead === 0
        ? 'أنت التالي على الخط'
        : ahead === 1
          ? 'أمامك متصل واحد'
          : ahead === 2
            ? 'أمامك متصلان'
            : ahead > 2
              ? `أمامك ${ahead} متصلين`
              : 'جارٍ تحديد موقعك في الطابور'
    : isVoiceLive
      ? otherSpeaking
        ? `${isAdmin ? 'المتصل' : 'المذيع'} يتحدث…`
        : 'تفضّل بالحديث'
      : voiceStatus === 'connecting'
        ? 'جارٍ فتح الصوت…'
        : voiceError || 'الصوت غير متصل';

  return (
    <div
      className="fixed inset-0 z-[70] bg-paper flex flex-col animate-fadeIn safe-top safe-bottom-edge"
      role="dialog"
      aria-modal="true"
      aria-label="مكالمة الاستديو"
    >
      <header className="px-4 pt-4 pb-2 flex items-center justify-between">
        <button
          onClick={() => setMinimized(true)}
          aria-label="تصغير"
          className="w-9 h-9 rounded-md flex items-center justify-center text-ink-muted hover:text-ink hover:bg-ink/5 transition-colors"
        >
          <Minus className="w-5 h-5" aria-hidden="true" />
        </button>
        <span className="text-xs text-ink-muted">
          {isWaiting ? 'انتظار على الخط' : 'مكالمة جارية'}
        </span>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-5">
        <span className="relative flex items-center justify-center">
          {/* نبضة واحدة تحمل كل الحركة في الشاشة */}
          {(isWaiting || otherSpeaking) && (
            <span className="absolute w-28 h-28 rounded-full bg-primary/15 animate-ping" />
          )}
          <span className="relative w-24 h-24 rounded-full bg-primary/10 text-primary flex items-center justify-center text-3xl font-semibold">
            {initial}
          </span>
        </span>

        <div className="space-y-1.5">
          <h2 className="text-xl font-semibold text-ink">{title}</h2>
          {call.topic && <p className="text-sm text-ink-muted leading-relaxed">{call.topic}</p>}
          {isAdmin && call.callerPhone && (
            <a
              href={`tel:${call.callerPhone.replace(/[^+\d]/g, '')}`}
              dir="ltr"
              className="text-xs text-primary font-mono block"
            >
              {call.callerPhone}
            </a>
          )}
        </div>

        <p className="text-sm text-ink-muted">{stateLine}</p>

        {isActive && (
          <p className="text-2xl font-semibold text-ink tabular-nums">{clock(elapsed)}</p>
        )}

        {/*
          المتصل يُخبَر أن مكالمته تُسجَّل.

          تسجيل صوت إنسان دون علمه ليس تفصيلاً في الواجهة: هو شرط في
          أنظمة كثيرة، وأمانة على كل حال. والإخبار قبل الحديث لا بعده.
        */}
        {isActive && (
          <p className="text-2xs text-ink-muted flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-live" aria-hidden="true" />
            <span>المكالمة تُسجَّل</span>
          </p>
        )}

        {isWaiting && !isAdmin && (
          <p className="text-xs text-ink-muted leading-relaxed max-w-xs">
            أبقِ التطبيق مفتوحاً حتى يردّ المذيع. البث متوقف الآن، ويعود بعد المكالمة.
          </p>
        )}

        {(errorMessage || voiceError) && (
          <p
            role="alert"
            className="text-xs text-live bg-live/10 border border-live/25 rounded-md px-3 py-2 flex items-start gap-1.5 max-w-xs"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-px" aria-hidden="true" />
            <span>{errorMessage || voiceError}</span>
          </p>
        )}
      </div>

      <div className="px-8 pb-10 flex items-center justify-center gap-6">
        {isActive && (
          <button
            onClick={() => setIsMuted((m) => !m)}
            disabled={!isVoiceLive}
            aria-label={isMuted ? 'إلغاء الكتم' : 'كتم الميكروفون'}
            className={`w-14 h-14 rounded-full flex items-center justify-center border transition-colors disabled:opacity-40 ${
              isMuted
                ? 'bg-ink text-paper border-ink'
                : 'bg-surface text-ink border-line hover:bg-surface-alt'
            }`}
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
        )}

        {/* على الهواء: المذيع يُصعد المتصل إلى مجلس الطيبين ليسمعه الحاضرون */}
        {isActive && isAdmin && (
          <button
            onClick={() => transferCall(call.id)}
            aria-label="تحويل المكالمة إلى الهواء في مجلس الطيبين"
            title="على الهواء"
            className="w-14 h-14 rounded-full flex flex-col items-center justify-center gap-0.5 border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
          >
            <Radio className="w-5 h-5" aria-hidden="true" />
            <span className="text-2xs font-semibold leading-none">الهواء</span>
          </button>
        )}

        <button
          onClick={() => (isWaiting && !isAdmin ? cancelCall() : endCall(call.id))}
          aria-label={isWaiting && !isAdmin ? 'إلغاء الاتصال' : 'إنهاء المكالمة'}
          className="w-16 h-16 rounded-full bg-live text-paper flex items-center justify-center shadow-lift"
        >
          <PhoneOff className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
